import { z } from 'zod';
import { ALL_TIME, ranges } from '@/utils/date-ranges';
import type { DateRange, Filter } from '@/types';

// Shape of search params persisted at /_auth/_side-layout. Lives in the URL
// so dashboard/events/users navigations are shareable, back/forward works,
// and route loaders can prefetch with the user's actual selection rather
// than a default placeholder.
//
// `range` stores a stable slug ('last_7d', 'custom', etc.) so renaming a
// preset label in the picker UI doesn't silently break previously shared
// links. Custom ranges also carry `from`/`to` ISO strings.
export const dashboardSearchSchema = z.object({
  range: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  filters: z
    .array(
      z.object({
        field: z.string().nullish(),
        operator: z.string().nullish(),
        value: z.string().nullish(),
        fieldName: z.string().nullish(),
        eventName: z.string().nullish(),
      }),
    )
    .optional(),
});

export type DashboardSearch = z.infer<typeof dashboardSearchSchema>;

// Stable URL slug ↔ presentational label. Slugs are part of the URL contract
// (shareable links must keep working); labels are UI copy and may change.
const RANGE_SLUG_TO_LABEL: Record<string, string> = {
  last_24h: 'Last 24 Hours',
  last_7d: 'Last 7 Days',
  last_30d: 'Last 30 Days',
  last_3m: 'Last 3 Months',
  last_6m: 'Last 6 Months',
  last_12m: 'Last 12 Months',
  all_time: ALL_TIME,
};
const RANGE_LABEL_TO_SLUG: Record<string, string> = Object.fromEntries(
  Object.entries(RANGE_SLUG_TO_LABEL).map(([slug, label]) => [label, slug]),
);

const findPresetBySlug = (slug: string | undefined) => {
  if (!slug) return undefined;
  // Primary: stable slug.
  const labelFromSlug = RANGE_SLUG_TO_LABEL[slug];
  if (labelFromSlug) return ranges.find((r) => r.label === labelFromSlug);
  // Backward compat: old links written with the raw label.
  return ranges.find((r) => r.label === slug);
};

export function dateRangeFromSearch(
  search: DashboardSearch,
  createdAt?: Date | string | null,
  isDemo = false,
): DateRange {
  const preset = findPresetBySlug(search.range);
  if (preset && preset.label !== 'Custom Range') {
    const r = preset.range();
    return {
      label: preset.label,
      interval: preset.interval,
      period: preset.period,
      startDate:
        preset.label === ALL_TIME && createdAt
          ? new Date(createdAt)
          : r.startDate,
      endDate: r.endDate,
      key: 'selection',
    };
  }
  if (search.range === 'custom' && search.from && search.to) {
    const startDate = new Date(search.from);
    const endDate = new Date(search.to);
    return {
      label: 'Custom Range',
      interval: '1 days',
      startDate,
      endDate,
      key: 'selection',
    };
  }
  // Default: 90 days for the demo (its full seeded window), else Last 30 Days.
  const fallback = isDemo ? ranges[3]! : ranges[2]!;
  const r = fallback.range();
  return {
    label: fallback.label,
    interval: fallback.interval,
    period: fallback.period,
    startDate: r.startDate,
    endDate: r.endDate,
    key: 'selection',
  };
}

export function dateRangeToSearch(range: DateRange): Partial<DashboardSearch> {
  if (range.label && range.label !== 'Custom Range') {
    const slug = RANGE_LABEL_TO_SLUG[range.label];
    return { range: slug ?? range.label, from: undefined, to: undefined };
  }
  return {
    range: 'custom',
    from: range.startDate.toISOString(),
    to: range.endDate.toISOString(),
  };
}

export function filtersFromSearch(search: DashboardSearch): Filter[] {
  return (search.filters ?? []) as Filter[];
}
